/**
 * Tvar vzhledu, jak ho posila API.
 *
 * Typy jsou tu opsane, ne sdilene z backendu: vyloha a API jsou dva balicky
 * bez spolecne zavislosti a jeden sdileny typ by z nich udelal jeden nasazovaci
 * celek. Zmena tvaru se pozna v `parseStorefront`, ne az za behu.
 */

export type Density = 'compact' | 'cozy' | 'roomy';
export type Font = 'system' | 'serif' | 'rounded';
export type Motion = 'off' | 'subtle' | 'full';

export interface StorefrontTheme {
  readonly accent: string;
  readonly accentInk: string;
  readonly accentSoft: string;
  /** Barva slev a cen po slevě – drží se zvlášť od značkové barvy. */
  readonly saleColor: string;
  /** Barva štítku „Novinka" nad zbožím – samostatná od akcentu i slevy. */
  readonly newColor: string;
  readonly radiusPx: number;
  readonly density: Density;
  readonly font: Font;
  readonly motion: Motion;
}

export type BlockKind =
  | 'hero'
  | 'heroCarousel'
  | 'usp'
  | 'categories'
  | 'products'
  | 'banner'
  | 'richtext'
  | 'promoTiles'
  | 'productRow';

export interface StorefrontBlock {
  readonly id: string;
  readonly kind: BlockKind;
  readonly position: number;
  readonly isVisible: boolean;
  readonly config: Record<string, unknown>;
}

export interface Storefront {
  readonly theme: StorefrontTheme;
  readonly blocks: readonly StorefrontBlock[];
}

export const DEFAULT_THEME: StorefrontTheme = {
  // „PetCenter feel" v naší zelené: akcent = tmavší značková zelená, měkký
  // odstín = značková zelená z loga, sleva = petcenter červená, radius 16.
  // Musí sedět s tokeny v styles.css – tahle záloha se propíše inline na
  // <html> při výpadku API, při náhledu v builderu i v bliknutí před načtením.
  accent: '#4c7a1c',
  accentInk: '#ffffff',
  accentSoft: '#7cb928',
  saleColor: '#d22630',
  // Indigová jako v styles.css (token --new). Musí sedět, aby náhled i výpadek
  // API vykreslily štítek „Novinka" ve stejné barvě jako uložený motiv.
  newColor: '#6b4de6',
  radiusPx: 16,
  density: 'cozy',
  font: 'system',
  motion: 'full',
};

/**
 * Zaloha, kdyz se vzhled nepodari nacist.
 *
 * Prazdna stranka pri vypadku endpointu vzhledu by vypadala jako rozbity
 * obchod. Katalog je to hlavni, co zakaznik potrebuje – ten se ukaze vzdycky.
 */
export const FALLBACK_BLOCKS: readonly StorefrontBlock[] = [
  {
    id: 'fallback-products',
    kind: 'products',
    position: 0,
    isVisible: true,
    config: { title: 'Naše zboží', showSearch: true, showCategories: true, limit: 24 },
  },
];

/** Pomocnici pro cteni konfigurace bloku – neznamy tvar nesmi shodit stranku. */
export function str(config: Record<string, unknown>, key: string, fallback = ''): string {
  const value = config[key];
  return typeof value === 'string' && value.trim() !== '' ? value : fallback;
}

export function num(config: Record<string, unknown>, key: string, fallback: number): number {
  const value = config[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function bool(config: Record<string, unknown>, key: string, fallback: boolean): boolean {
  const value = config[key];
  return typeof value === 'boolean' ? value : fallback;
}

export function items(
  config: Record<string, unknown>,
  key: string,
): readonly Record<string, unknown>[] {
  const value = config[key];
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is Record<string, unknown> =>
      typeof item === 'object' && item !== null && !Array.isArray(item),
  );
}

/**
 * Odkaz z konfigurace.
 *
 * Backend uz pousti jen cesty a kotvy, ale vyloha dostava data i z nahledu
 * v builderu (postMessage), kde zadnou validaci neprosly. Kontrola se proto
 * dela znovu tady – druha obrana na hranici, kde se z dat stava `href`.
 */
export function safeHref(config: Record<string, unknown>, key: string): string | null {
  const value = str(config, key);
  if (value === '') return null;
  if (value.startsWith('/') || value.startsWith('#')) return value;
  return null;
}

export function safeImage(config: Record<string, unknown>, key: string): string | null {
  const value = str(config, key);
  return value.startsWith('/uploads/') ? value : null;
}
